import type { PayloadGroup } from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import { GroupStatus } from './groupStatus';

const unavailableGroupStatuses = [
  GroupStatus.DELETED,
  GroupStatus.REVOKED,
  GroupStatus.ARCHIVED,
];

const isUpdateResolved = (
  group: PayloadGroup,
  autoUpdateMapping: Record<string, string>,
): boolean => {
  const needsAutomaticMapping = [
    GroupStatus.MAJOR_UPDATE,
    GroupStatus.MINOR_UPDATE_WITH_AUTO_MAPPING_SLIDES,
  ].includes(group.groupStatus);

  if (!needsAutomaticMapping) return true;
  if (group.pages.length === 1) return Boolean(autoUpdateMapping[group.pages[0].pageId]);
  if (group.pages.length > 1) return Boolean(autoUpdateMapping[group.id]);
  return true;
};

export const canAddSlides = (
  selectedGroups: PayloadGroup[],
  autoUpdateMapping: Record<string, string>,
  isLocked: boolean | undefined,
): boolean => !isLocked && selectedGroups.every(group => (
  !unavailableGroupStatuses.includes(group.groupStatus) &&
  isUpdateResolved(group, autoUpdateMapping)
));

export const isSingleAndHasAssociation = (
  group: PayloadGroup,
  selectedGroups: PayloadGroup[],
  associatedParentsMap: ReadonlyMap<string, boolean>,
): boolean => {
  let canRemove = true;
  group.pages.forEach(page => {
    if (associatedParentsMap.get(page.pageId)) {
      let count = 0;
      selectedGroups.forEach(group => {
        group.pages.forEach(p => {
          if (p.pageId === page.pageId) count++;
        });
      });
      if (count < 2) canRemove = false;
    }
  });
  return !canRemove;
};

export const canRemoveGroup = (
  group: PayloadGroup,
  selectedGroups: PayloadGroup[],
): boolean => {
  const pageIdRequiredCountMap = group.documentVersionORM?.meta.allPages.reduce<Record<string, number>>(
    (acc, page) => {
      if (page.isRequired) {
        const selectedRequiredCount = selectedGroups.reduce<number>(
          (acc, grp) => {
            if (grp.documentVersionORM === group.documentVersionORM) {
              grp.pages.forEach(pg => {
                if (pg.isRequired && pg.pageId === page.pageId)
                { acc++; }
              });
            }

            return acc;
          },
          0,
        );

        acc[page.pageId] = selectedRequiredCount;
      }
      return acc;
    },
    { },
  ) ?? { };

  const canRemove = group.pages.every(page => {
    const requiredCount = pageIdRequiredCountMap[page.pageId];

    return requiredCount !== undefined
      ? requiredCount > 1
      : true;
  });

  return canRemove;
};
export const hasRequiredHiddenSlides = (
  selectedGroups: PayloadGroup[],
  associatedParentsMap: ReadonlyMap<string, boolean>,
): boolean => selectedGroups.some(group => group.pages.some(page => (
  (!group.visible || page.isHidden) && (page.isRequired || associatedParentsMap.get(page.pageId))
)));

// Header availability only; title validation and hidden-slide confirmation remain separate.
export const canSaveGroups = (
  selectedGroups: PayloadGroup[],
  isLocked: boolean | undefined,
  isOnline: boolean,
  allContentCached: boolean,
): boolean => !isLocked && selectedGroups.length > 0 &&
  selectedGroups.some(({ visible }) => visible) && (isOnline || allContentCached);
